import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('access_token') : null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for stored token on mount
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Global 401 interceptor: auto-logout when any authenticated request returns 401
  useEffect(() => {
    if (typeof window === 'undefined' || window.__mid2bidFetchPatched) return;
    window.__mid2bidFetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    const handleExpiredSession = () => {
      const hadSession = !!localStorage.getItem('access_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      if (hadSession && !['/login', '/signup', '/forgot-password', '/verify'].some(p => window.location.pathname.startsWith(p))) {
        // Use sessionStorage so the login page can surface an "expired" notice.
        try { sessionStorage.setItem('mid2bid.sessionExpired', '1'); } catch (_) { /* ignore */ }
        window.location.href = '/login';
      }
    };

    // Attempt to refresh the access_token using the stored refresh_token.
    // Returns the new access token on success, null on failure. Single-flight
    // via a shared promise so multiple concurrent 401s trigger only one refresh.
    const tryRefresh = async () => {
      if (window.__mid2bidRefreshInFlight) return window.__mid2bidRefreshInFlight;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return null;
      window.__mid2bidRefreshInFlight = (async () => {
        // WO-06 item 3 — a throttled (429) or briefly-unreachable /refresh is NOT
        // an expired session. Retry a couple of times with a growing gap
        // (honouring Retry-After), and only log the user out when the refresh is
        // genuinely rejected (401 / expired-or-invalid token).
        const delays = [0, 800, 2000];
        try {
          for (let attempt = 0; attempt < delays.length; attempt++) {
            if (delays[attempt]) await new Promise((r) => setTimeout(r, delays[attempt]));
            let resp;
            try {
              resp = await originalFetch(`${API_URL}/api/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
              });
            } catch (_) {
              continue; // network blip — temporary, keep the session
            }
            if (resp.status === 429) {
              const ra = parseInt(resp.headers.get('Retry-After') || '0', 10);
              if (ra > 0 && ra <= 5 && attempt < delays.length - 1) {
                await new Promise((r) => setTimeout(r, ra * 1000));
              }
              continue; // throttled — retry, do NOT log out
            }
            if (!resp.ok) return null; // genuine auth failure — caller logs out
            const data = await resp.json().catch(() => null);
            if (!data?.access_token) return null;
            localStorage.setItem('access_token', data.access_token);
            if (data.refresh_token) {
              localStorage.setItem('refresh_token', data.refresh_token);
            }
            setToken(data.access_token);
            return data.access_token;
          }
          return 'TEMPORARY'; // exhausted retries on 429/network — keep session
        } finally {
          // Release the single-flight lock on the next tick
          setTimeout(() => { window.__mid2bidRefreshInFlight = null; }, 0);
        }
      })();
      return window.__mid2bidRefreshInFlight;
    };

    window.fetch = async (input, init = {}) => {
      const response = await originalFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        // BUG-C09 FIX: only treat a request as authenticated when it actually
        // carries an Authorization header. Do NOT fall back to reading the
        // token from localStorage — that produced false positives for
        // unauthenticated requests and risked acting on stale tokens.
        const hasAuthHeader = !!(init && init.headers && (
          (init.headers.Authorization) ||
          (init.headers.get && init.headers.get('Authorization'))
        ));
        if (
          response.status === 401 &&
          hasAuthHeader &&
          url.includes('/api/') &&
          !url.includes('/api/auth/login') &&
          !url.includes('/api/auth/signup') &&
          !url.includes('/api/auth/forgot-password') &&
          !url.includes('/api/auth/reset-password') &&
          !url.includes('/api/auth/refresh')
        ) {
          // Try silent refresh once before giving up
          const newToken = await tryRefresh();
          if (typeof newToken === 'string' && newToken !== 'TEMPORARY') {
            // Retry original request with fresh token
            const newInit = { ...init, headers: { ...(init.headers || {}) } };
            // Normalize headers to plain object
            if (newInit.headers instanceof Headers) {
              const obj = {};
              newInit.headers.forEach((v, k) => { obj[k] = v; });
              newInit.headers = obj;
            }
            newInit.headers.Authorization = `Bearer ${newToken}`;
            return originalFetch(input, newInit);
          }
          if (newToken === 'TEMPORARY') {
            // WO-06 item 3 — refresh was throttled (429) or unreachable, not
            // rejected. Keep the session usable and return the original response
            // instead of flashing the user to the sign-in screen.
            return response;
          }
          handleExpiredSession();
        }
        if (response.status === 402 && url.includes('/api/')) {
          // A plan refusal may mean the account's entitlements are stale (e.g.
          // just upgraded elsewhere). Ask the EntitlementsProvider to re-fetch.
          try { window.dispatchEvent(new Event('entitlements:refetch')); } catch (_) {}
        }
      } catch (_) {
        /* swallow — interceptor must never throw */
      }
      return response;
    };
  }, []);

  const signup = async (data) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        // BUG-S03 FIX: show retry-after time for 429 rate limit errors
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const wait = retryAfter ? ` Please wait ${retryAfter} seconds before trying again.` : ' Please wait a moment before trying again.';
          throw new Error(`Too many signup attempts.${wait}`);
        }
        const detail = result.detail;
        const errorMsg = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map(e => e.msg || JSON.stringify(e)).join('. ')
            : 'Signup failed';
        throw new Error(errorMsg);
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const verifyEmail = async (email, code) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        // WO-03 item 3 — 429 first (verify-email is a screen a new customer meets).
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const wait = retryAfter
            ? ` Please wait ${retryAfter} seconds before trying again.`
            : ' Please wait a moment before trying again.';
          throw new Error(`Too many attempts.${wait}`);
        }
        const detail = result.detail || result.error;
        throw new Error(typeof detail === 'string' ? detail : 'Verification failed');
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // WO-21 — ask the server for a NEW signup verification code. The endpoint
  // answers the same way whether or not the address is waiting to be verified
  // (it must not tell a stranger which emails are registered), so a success
  // here means "the request was accepted", not "an email definitely went out".
  // Anything that is not a 2xx must reach the caller as an error: the old
  // Resend button claimed success without calling anything at all.
  const resendVerification = async (email) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const wait = retryAfter
            ? ` Please wait ${retryAfter} seconds before trying again.`
            : ' Please wait a moment before trying again.';
          throw new Error(`Too many requests.${wait}`);
        }
        const detail = result.detail || result.error;
        throw new Error(typeof detail === 'string' ? detail : 'Could not send a new code');
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        // WO-03 item 3 — handle 429 FIRST. This endpoint family answers a rate
        // limit with `error` and everything else with `detail`; reading only
        // one is how the locked-out customer got told their password was wrong.
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const serverMsg = result && (result.detail || result.error);
          if (typeof serverMsg === 'string' && serverMsg) throw new Error(serverMsg);
          const wait = retryAfter
            ? ` Please wait ${retryAfter} seconds before trying again.`
            : ' Please wait a moment before trying again.';
          const waitAr = retryAfter
            ? ` الرجاء الانتظار ${retryAfter} ثانية قبل المحاولة مرة أخرى.`
            : ' الرجاء الانتظار لحظة قبل المحاولة مرة أخرى.';
          throw new Error(`Too many sign-in attempts.${wait} / محاولات تسجيل دخول كثيرة جداً.${waitAr}`);
        }
        const detail = result.detail || result.error;
        throw new Error(typeof detail === 'string' ? detail : 'Login failed');
      }
      
      // Store tokens and user
      localStorage.setItem('access_token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      localStorage.setItem('user', JSON.stringify(result.user));
      
      setToken(result.access_token);
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('access_token');
    // Revoke server-side refresh token so the session cannot be replayed.
    if (accessToken) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
      } catch (_) {
        /* network errors shouldn't block client-side logout */
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  const value = {
    user,
    loading,
    error,
    signup,
    verifyEmail,
    resendVerification,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    token,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
